import fireAddressCaptured from "@/lib/tagular/fire-address-captured";
import toTitleCase from "@/lib/to-title-case";
import {
  PLAN_BUILDER_INTERNET_URL,
  resolveServiceabilityDestinationUrl,
} from "@/lib/clickthrough-destination";
import type {
  ServiceabilityAddressSuggestion,
  ServiceabilityV3Response,
  ServiceabilityV3ResponseMessage,
} from "@/types/serviceability";

export type ServiceabilityRouteTarget =
  | "planBuilder"
  | "activeAddress"
  | "business"
  | "addressBlocked"
  | "salesError"
  | "community"
  | "outOfFootprint"
  | "unknown";

export type ServiceabilityDestinationSource =
  | "apiRedirect"
  | "custom"
  | "defaultPlanBuilder"
  | "serviceabilityOutcome";

export interface ServiceabilityDestinationDecision {
  url: string;
  source: ServiceabilityDestinationSource;
}

const TRUSTED_SERVICEABILITY_REDIRECT_HOSTS = new Set([
  "www.xfinity.com",
  "www.stg.xfinity.com",
]);

const normalizeHostname = (hostname: string): string =>
  hostname.toLowerCase().replace(/\.$/, "");

const getTrustedServiceabilityRedirectUrl = (rawUrl: string): URL | null => {
  const trimmedUrl = rawUrl.trim();
  if (!trimmedUrl) {
    return null;
  }

  const baseUrl = new URL(
    process.env.NEXT_PUBLIC_BASE_URL || "https://www.xfinity.com"
  );

  if (trimmedUrl.startsWith("/") && !trimmedUrl.startsWith("//")) {
    return new URL(trimmedUrl, baseUrl);
  }

  try {
    const redirectUrl = new URL(trimmedUrl);
    const trustedHosts = new Set([
      ...TRUSTED_SERVICEABILITY_REDIRECT_HOSTS,
      normalizeHostname(baseUrl.hostname),
    ]);

    if (
      redirectUrl.protocol === "https:" &&
      trustedHosts.has(normalizeHostname(redirectUrl.hostname))
    ) {
      return redirectUrl;
    }
  } catch {
    return null;
  }

  return null;
};

const getFirstResponseMessage = (
  response: ServiceabilityV3Response
): ServiceabilityV3ResponseMessage | undefined =>
  response.responseMessages.find(({ serviceMessageCode, code }) =>
    Boolean(code || serviceMessageCode)
  );

const responseMessageMatches = (
  { code, serviceMessageCode }: ServiceabilityV3ResponseMessage,
  codes: readonly string[]
): boolean =>
  codes.includes(code ?? "") || codes.includes(serviceMessageCode ?? "");

const hasResponseMessage = (
  response: ServiceabilityV3Response,
  codes: readonly string[]
): boolean =>
  response.responseMessages.some((message) =>
    responseMessageMatches(message, codes)
  );

const getFirstMatchingResponseMessage = (
  response: ServiceabilityV3Response,
  codes: readonly string[]
): ServiceabilityV3ResponseMessage | undefined =>
  response.responseMessages.find((message) =>
    responseMessageMatches(message, codes)
  );

const getAddressParamValue = (
  value: string | null | undefined,
  fallbackValue: string
): string => {
  if (value && value.trim()) {
    return value;
  }

  return fallbackValue;
};

const buildApartmentParam = (
  address: ServiceabilityAddressSuggestion,
  serviceabilityAddress: ServiceabilityV3Response["address"]
): string => {
  const serviceabilityApt = serviceabilityAddress?.streetAddress2;
  if (serviceabilityApt && serviceabilityApt.trim()) {
    return serviceabilityApt;
  }

  const addressApt = address.streetAddress2;
  if (addressApt && addressApt.trim()) {
    return addressApt;
  }

  if (address.unitType && address.unitNumber) {
    return `${address.unitType} ${address.unitNumber}`;
  }

  if (address.unitNumber) {
    return address.unitNumber;
  }

  return "";
};

const buildAddressParams = (
  address: ServiceabilityAddressSuggestion,
  serviceabilityAddress: ServiceabilityV3Response["address"]
): URLSearchParams =>
  new URLSearchParams({
    StreetAddress: getAddressParamValue(
      serviceabilityAddress?.streetAddress1,
      address.streetAddress1
    ),
    Apartment: buildApartmentParam(address, serviceabilityAddress),
    ZipCode: getAddressParamValue(
      serviceabilityAddress?.zipCode,
      address.zipCode
    ),
  });

const appendAddressParamsToUrl = (
  rawUrl: string,
  address: ServiceabilityAddressSuggestion,
  serviceabilityAddress: ServiceabilityV3Response["address"]
): string | null => {
  const redirectUrl = getTrustedServiceabilityRedirectUrl(rawUrl);
  if (!redirectUrl) {
    return null;
  }

  buildAddressParams(address, serviceabilityAddress).forEach((value, key) => {
    redirectUrl.searchParams.set(key, value);
  });

  return redirectUrl.toString();
};

const buildOutOfFootprintUrl = (
  address: ServiceabilityAddressSuggestion,
  serviceabilityAddress: ServiceabilityV3Response["address"]
): string => {
  const params = buildAddressParams(address, serviceabilityAddress);

  return `${process.env.NEXT_PUBLIC_BASE_URL}/learn/landing/out-of-footprint?${params.toString()}`;
};

const buildCommunityUrl = (
  address: ServiceabilityAddressSuggestion,
  serviceabilityAddress: ServiceabilityV3Response["address"]
): string => {
  const params = buildAddressParams(address, serviceabilityAddress);

  return `${process.env.NEXT_PUBLIC_BASE_URL}/learn/landing/bulk-master?${params.toString()}`;
};

const buildSalesErrorUrl = (response: ServiceabilityV3Response): string => {
  const errorCode = getSalesResponseCode(response);
  const params = new URLSearchParams(errorCode ? { ErrorCode: errorCode } : {});
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return `${process.env.NEXT_PUBLIC_BASE_URL}/learn/landing/sorry${suffix}`;
};

const isOutOfFootprintResponse = (
  response: ServiceabilityV3Response
): boolean =>
  hasResponseMessage(response, [
    "OutOfFootPrint",
    "LocationCapabilitiesEmptyForAddress",
    "MultipleCustomerProfile",
  ]);

const isBusinessResponse = (response: ServiceabilityV3Response): boolean =>
  hasResponseMessage(response, ["BulkNotPermitted"]);

const isAddressBlockedResponse = (
  response: ServiceabilityV3Response
): boolean =>
  hasResponseMessage(response, ["LocationCapabilitiesFraudIndicator"]);

const isSalesErrorResponse = (response: ServiceabilityV3Response): boolean =>
  hasResponseMessage(response, ["SalesError"]);

const getSalesResponseCode = (response: ServiceabilityV3Response): string => {
  const salesResponseCode = getFirstMatchingResponseMessage(response, [
    "SalesError",
  ]);

  const rawCode =
    salesResponseCode?.serviceMessageCode || salesResponseCode?.detail || "";
  const normalized = String(rawCode).split(";")[0]?.trim() ?? "";
  return normalized;
};

const isCommunityResponse = (response: ServiceabilityV3Response): boolean =>
  hasResponseMessage(response, ["BulkMasterDwellType"]);

export const getResponseMessageCode = (
  response: ServiceabilityV3Response
): string => {
  const responseMessage = getFirstResponseMessage(response);

  return responseMessage?.code || "COULDNT_PARSE_CODE";
};

export const getResponseServiceMessageCode = (
  response: ServiceabilityV3Response
): string => {
  const responseMessage = getFirstResponseMessage(response);

  return responseMessage?.serviceMessageCode || "NotProvided";
};

const isServiceException = (response: ServiceabilityV3Response): boolean =>
  hasResponseMessage(response, ["ServiceException"]);

export const getRouteTarget = (
  response: ServiceabilityV3Response
): ServiceabilityRouteTarget => {
  if (response.locationAttributes?.activeAccount) {
    return "activeAddress";
  }

  if (isServiceException(response)) {
    return "activeAddress";
  }

  if (isBusinessResponse(response)) {
    return "business";
  }

  if (isAddressBlockedResponse(response)) {
    return "addressBlocked";
  }

  if (isSalesErrorResponse(response)) {
    return "salesError";
  }

  if (isCommunityResponse(response)) {
    return "community";
  }

  if (
    response.locationAttributes?.notServiceableArea ||
    isOutOfFootprintResponse(response)
  ) {
    return "outOfFootprint";
  }

  if (response.responseStatus === "Success") {
    return "planBuilder";
  }

  return "unknown";
};

export const getServiceabilityTrackingAttributes = ({
  address,
  response,
  routeTarget,
}: {
  address: ServiceabilityAddressSuggestion;
  response?: ServiceabilityV3Response;
  routeTarget?: ServiceabilityRouteTarget;
}): Record<string, string> => ({
  endpoint: "serviceability_v3",
  hasLocationId: String(Boolean(address.locationId)),
  hasElocId: String(Boolean(address.elocId)),
  hasGeofabricId: String(Boolean(address.geofabricId)),
  ...(response
    ? {
        responseStatus: response.responseStatus,
        hasSalesSessionId: String(Boolean(response.salesSessionId)),
        salesSessionId: response.salesSessionId ?? "",
        activeAccount: String(
          Boolean(response.locationAttributes?.activeAccount)
        ),
        responseMessageCode: getResponseMessageCode(response),
        responseServiceMessageCode: getResponseServiceMessageCode(response),
      }
    : {}),
  ...(routeTarget ? { routeTarget } : {}),
});

export const getTypeaheadTrackingAttributes = (
  hasSelectedAddress: boolean
): Record<string, string> => ({
  endpoint: "getaddress",
  hasSelectedAddress: String(hasSelectedAddress),
});

export const getNextUrlDecision = (
  address: ServiceabilityAddressSuggestion,
  response: ServiceabilityV3Response,
  overridePlanBuilderUrl?: string
): ServiceabilityDestinationDecision => {
  if (response.redirectUrl?.trim()) {
    const redirectUrl = appendAddressParamsToUrl(
      response.redirectUrl,
      address,
      response.address
    );
    if (redirectUrl) {
      return { url: redirectUrl, source: "apiRedirect" };
    }
  }

  const routeTarget = getRouteTarget(response);

  if (routeTarget === "activeAddress") {
    return {
      url: `${process.env.NEXT_PUBLIC_BASE_URL}/learn/landing/active-address`,
      source: "serviceabilityOutcome",
    };
  }

  if (routeTarget === "business") {
    return {
      url: `${process.env.NEXT_PUBLIC_BASE_URL}/learn/landing/business-location`,
      source: "serviceabilityOutcome",
    };
  }

  if (routeTarget === "addressBlocked") {
    return {
      url: `${process.env.NEXT_PUBLIC_BASE_URL}/learn/landing/attention`,
      source: "serviceabilityOutcome",
    };
  }

  if (routeTarget === "salesError") {
    return {
      url: buildSalesErrorUrl(response),
      source: "serviceabilityOutcome",
    };
  }

  if (routeTarget === "community") {
    return {
      url: buildCommunityUrl(address, response.address),
      source: "serviceabilityOutcome",
    };
  }

  if (routeTarget === "outOfFootprint") {
    return {
      url: buildOutOfFootprintUrl(address, response.address),
      source: "serviceabilityOutcome",
    };
  }

  if (routeTarget === "planBuilder") {
    const customUrl = resolveServiceabilityDestinationUrl(overridePlanBuilderUrl);
    return customUrl
      ? { url: customUrl, source: "custom" }
      : { url: PLAN_BUILDER_INTERNET_URL, source: "defaultPlanBuilder" };
  }

  return {
    url: buildOutOfFootprintUrl(address, response.address),
    source: "serviceabilityOutcome",
  };
};

export const getNextUrl = (
  address: ServiceabilityAddressSuggestion,
  response: ServiceabilityV3Response,
  overridePlanBuilderUrl?: string
): string =>
  getNextUrlDecision(address, response, overridePlanBuilderUrl).url;

export const formatAndCaptureAddress = (
  selectedAddress: ServiceabilityAddressSuggestion
) => {
  if (!selectedAddress) return;

  const streetAddress1 = selectedAddress.streetAddress1.trim();
  const streetAddress2 = selectedAddress.streetAddress2.trim();
  const unitSegment = `${selectedAddress.unitType ?? ""} ${
    selectedAddress.unitNumber ?? ""
  }`.trim();
  const unitDisplayValue = streetAddress2
    ? toTitleCase(streetAddress2)
    : unitSegment
      ? toTitleCase(unitSegment)
      : "";

  const formattedCapturedAddress = [
    toTitleCase(streetAddress1),
    unitDisplayValue,
    toTitleCase(selectedAddress.city.trim()),
    selectedAddress.state.trim().toUpperCase(),
    selectedAddress.zipCode.trim(),
  ].join(", ");

  const zip4Value = selectedAddress.zip4Code?.trim() ?? "";
  const zip4Parsed = zip4Value ? Number.parseInt(zip4Value, 10) : undefined;
  const zip4 = Number.isFinite(zip4Parsed) ? zip4Parsed : undefined;
  const addressId =
    selectedAddress.locationId ??
    selectedAddress.geofabricId ??
    selectedAddress.elocId ??
    undefined;

  fireAddressCaptured({
    activityDateTime: new Date().toISOString(),
    address: formattedCapturedAddress,
    addressId,
    postalCode: selectedAddress.zipCode,
    zip4,
    integrationContext: {
      salesflow: "SITE",
    },
  });
};
